import { Store, StoreSettings } from 'shop/components/Landing/types'
import { CategoryWithProducts } from 'shop/components/Shop/Categories'
import { AffectedProduct } from 'shop/types/cart'
import { mapKeysToNewProperty } from 'shop/utils/common'

// New queried data uses paths rather than querying the whole jsonb "settings".
type RawStore = Omit<Store, 'settings'> & StoreSettings
/**
 * Now we are directly querying the store settings with paths, we need to put them back into their settings object.
 * This is to not incur a large amount of changes whilst referencing store.settings across the code.
 */
export const mapStoreSettingsIntoObject = (stores: RawStore[]): Store[] => {
  const storeSettingsKeys = [
    'delivery_radius',
    'scheduled_delivery',
    'scheduled_pickup',
    'same_day_pickup',
    'same_day_delivery',
    'table_enabled',
    'table_welcome_message',
    'table_count',
    'table_allow_counter_pickup',
    'table_counter_pickup_message',
    'table_choices',
    'asap_only',
    'asap_enabled',
    'delivery_interval',
    'pickup_interval',
    'busy_preparation_time',
    'moderate_preparation_time',
    'quiet_preparation_time',
    'prep_mode',
    'delivery_area',
    'busy_delivery_area',
    'busy_delivery_area_enabled'
  ]

  const mappedStores = stores.map((store) =>
    mapKeysToNewProperty(store as Object, storeSettingsKeys, 'settings')
  ) as Store[]
  return mappedStores
}

/** Applies discount pricing to products when a promotion is active. When a discount/promotion is applied to the cart, the BE returns an array
 * of affected products with their discounted prices. This function merges the discounted prices back into the original product data,
 * updating base, variant and modifier prices */
export const patchProducts = (
  categories: CategoryWithProducts[],
  affectedProducts: AffectedProduct[]
) => {
  return categories.map((category) => {
    // Patch each product in the category
    const patchedProducts = category.products.map((product) => {
      const affectedProduct = affectedProducts.find(
        (affected) => affected.id === product.id
      )

      if (affectedProduct) {
        // Patch modifier groups
        const patchedModifierGroups = product.modifierGroups.map(
          (modifierGroup) => {
            const affectedModifierGroup = affectedProduct.modifierGroups.find(
              (affectedModGroup) => affectedModGroup.id === modifierGroup.id
            )
            if (affectedModifierGroup) {
              // Patch modifiers
              const patchedModifiers = modifierGroup.modifiers.map(
                (modifier) => {
                  const affectedMod = affectedModifierGroup.modifiers.find(
                    (am) => am.id === modifier.id
                  )
                  return affectedMod
                    ? { ...modifier, price: affectedMod.price }
                    : modifier
                }
              )
              return { ...modifierGroup, modifiers: patchedModifiers }
            }
            return modifierGroup
          }
        )

        // Patch variants
        const patchedVariants = product.variants.map((variant) => {
          const affectedVariant = affectedProduct.variants.find(
            (av) => av.id === variant.id
          )
          return affectedVariant
            ? {
                ...variant,
                pricing: {
                  ...variant.pricing,
                  absolute: affectedVariant.pricing.absolute,
                  relative: affectedVariant.pricing.relative
                }
              }
            : variant
        })

        // Patch pricing
        const patchedPricing = affectedProduct.pricing
        const patchedPromotions = affectedProduct.promotion

        // Return patched product
        return {
          ...product,
          modifierGroups: patchedModifierGroups,
          variants: patchedVariants,
          pricing: patchedPricing,
          promotion: patchedPromotions
        }
      }

      // If not affected, return original product
      return product
    })

    // Return new category with patched products
    return {
      ...category,
      products: patchedProducts
    }
  })
}
